// Provider-agnostic message shapes and helpers.
//
// Extracted (S1) from src/features/chat/firestore.js so neither the firebase
// nor the supabase provider re-implements them. The ONLY behavioral change vs
// firestore.js is that `normalizeMessage` now takes a plain `{ id, data }`
// object instead of a Firestore docSnap — both providers adapt their native
// row to that shape before calling:
//   - firebase (S2): normalizeMessage({ id: docSnap.id, data: docSnap.data() })
//   - supabase (S4): normalizeMessage({ id: row.id, data: row })
//
// `sortKey` and `newMessageId` are copied verbatim — they were already
// docSnap-free. Timestamp normalization is NOT done here: providers normalize
// `sentAt` to epoch-ms-or-null BEFORE handing data in, so `sortKey` still sees
// either a number or null (its `.toMillis` branch is retained as a defensive
// no-op for any provider that hasn't pre-normalized).
//
// During S1 this file DUPLICATES the logic still living in firestore.js; that
// is intentional and temporary — S2 makes the old module delegate here.

// Id generator — must always return a valid RFC-4122 UUID, because the Supabase
// `messages.id` column is type `uuid` and rejects anything else (the Firestore
// provider accepts any string, but the shared id has to satisfy both).
//
// `crypto.randomUUID` only exists in SECURE contexts (https or localhost). When
// the app is opened over a plain-http LAN IP (e.g. a phone hitting
// http://192.168.x.x:3000 during dev) it's undefined — so we build a v4 UUID
// from `crypto.getRandomValues` (which IS available in insecure contexts), and
// fall back to Math.random only if even that is missing (old test envs).
export function newMessageId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC-4122 variant
  const h = [...bytes].map((b) => b.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h
    .slice(6, 8)
    .join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
}

// Extract a sortable timestamp from a normalized message.
//
//   1. Prefer the Firestore serverTimestamp `sentAt` (new-shape docs).
//   2. Numeric sentAt (rare; defensive).
//   3. Fall back to parsing the doc id as `Date.now()` ms — legacy docs
//      used `'' + Date.now()` as their id, so the id IS the timestamp.
//   4. Optimistic and otherwise-unsortable rows return Date.now() so they
//      slot in at the tail (where the user expects fresh content).
//
// Why this matters: an `orderBy('sentAt','desc')` query in Firestore
// implicitly excludes documents missing the sentAt field — every legacy
// GLOB message from before the schema migration silently drops out of
// the result set. Sorting client-side with this fallback brings them
// back, at the cost of pulling the full room's doc set on subscribe.
export function sortKey(m) {
  const t = m.sentAt;
  if (t && typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t === 'number') return t;
  const idAsNum = Number(m.id);
  if (Number.isFinite(idAsNum) && idAsNum > 0) return idAsNum;
  return Date.now();
}

// Normalize a backend row to the shape Bubble expects, regardless of whether
// the row was written before or after the Phase 05 migration. Takes a plain
// `{ id, data }` object — `data` is the raw field map (legacy or new shape).
export function normalizeMessage({ id, data }) {
  data = data || {};
  const senderId = data.senderId ?? data.user ?? '';
  return {
    id,
    text: data.text ?? data.message ?? '',
    senderId,
    senderName: data.senderName ?? data.userName ?? '',
    avatarSeed: data.avatarSeed ?? senderId ?? '',
    code: data.code ?? '',
    // sentAt may be null while a server write is in flight; callers should
    // show "just now" until it resolves. Providers pass it through as-is
    // (already normalized to epoch-ms-or-null before calling).
    sentAt: data.sentAt ?? null,
    replyTo: data.replyTo ?? null,
    reactions: data.reactions ?? {},
    // Beam attachments: an optional embedded payload that the bubble
    // renders instead of (or above) plain text. Supported kinds:
    //   - 'code' (F12.1)  — snippet bubble
    //   - 'file' (F11.1+) — FileCard with live progress
    // The schema now has messages.attachment, so preserve it if present.
    attachment: data.attachment ?? null,
  };
}

// Keep the legacy test-import surface working so existing tests resolve after
// the S3 shims repoint here. (firestore.js currently exports the same triple.)
export const __test__ = { normalizeMessage, newMessageId, sortKey };
